import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { ApiHttpError, sendPhoneCode, verifyPhoneCode } from '@/api/auth'

export type EtapeCode = 'idle' | 'envoye' | 'ok'

/**
 * Cellulaire vérifié par code texto : « Envoyer le code » → 6 chiffres → « Vérifier ».
 * Le numéro est saisi par le parent (PhoneInput) ; ici on gère l'envoi, le code,
 * les essais et les erreurs en mots simples. Même bloc à l'onboarding (Bienvenue)
 * et sur la page dédiée après une invitation.
 */
export function PhoneVerification({ phone, locale, etape, onEtape, envoyer, verifier, sombre = true }: {
  phone: string
  locale: string
  etape: EtapeCode
  onEtape: (e: EtapeCode) => void
  /** Par défaut : le compte connecté. L'invité passe ses propres appels (jeton d'invitation). */
  envoyer?: (phone: string, locale: string) => Promise<unknown>
  verifier?: (code: string) => Promise<unknown>
  /** Fond sombre (écran Bienvenue) ou clair (formulaire d'invitation). */
  sombre?: boolean
}) {
  const { t } = useTranslation()
  const [code, setCode] = useState('')
  const [erreurCode, setErreurCode] = useState('')
  const [attente, setAttente] = useState(0)

  // Compte à rebours avant de pouvoir renvoyer un code.
  useEffect(() => {
    if (attente <= 0) return
    const id = setTimeout(() => setAttente((a) => a - 1), 1000)
    return () => clearTimeout(id)
  }, [attente])

  // `secours` : le message quand le code est inconnu. Une vérification ratée ne
  // dit pas « Impossible d'envoyer le code » : rien n'était à envoyer.
  const codeErreur = (err: unknown, secours: 'generic' | 'verify_failed') => {
    // Le serveur renvoie un code court (mobile_required, phone_already_used…) : on le dit en mots.
    const data = err instanceof ApiHttpError ? err.data : isAxiosError(err) ? err.response?.data : undefined
    const c = (data as { error?: string } | undefined)?.error || ''
    const message = t(`onboarding.welcome.verif.errors.${secours}`)
    return c ? t(`onboarding.welcome.verif.errors.${c}`, message) : message
  }

  const envoyerMutation = useMutation({
    mutationFn: () => (envoyer ?? sendPhoneCode)(phone, locale),
    onSuccess: () => { onEtape('envoye'); setCode(''); setErreurCode(''); setAttente(60) },
    onError: (err) => setErreurCode(codeErreur(err, 'generic')),
  })

  const verifierMutation = useMutation({
    mutationFn: () => (verifier ?? verifyPhoneCode)(code),
    onSuccess: () => { onEtape('ok'); setErreurCode('') },
    onError: (err) => setErreurCode(codeErreur(err, 'verify_failed')),
  })

  const etapeCode = etape
  const personalPhone = phone
  if (etape === 'ok') return null
  // Deux habillages, même comportement.
  const boite = sombre
    ? { backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.1)' }
    : { backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0' }
  const champ = sombre
    ? { backgroundColor: '#0A192F', border: '1px solid rgba(255,255,255,0.15)', color: 'white' }
    : { backgroundColor: 'white', border: '1px solid #CBD5E1', color: '#0F172A' }
  const bouton = sombre ? { backgroundColor: 'white', color: '#0A192F' } : { backgroundColor: '#0A192F', color: 'white' }
  const discret = { color: sombre ? '#94A3B8' : '#64748B' }

  return (
              <div className="flex flex-col text-left gap-3 rounded-lg p-4" style={boite}>
                {etapeCode === 'idle' ? (
                  <button
                    type="button"
                    onClick={() => envoyerMutation.mutate()}
                    disabled={envoyerMutation.isPending || (personalPhone || '').replace(/\D/g, '').length < 8}
                    className="h-11 px-4 rounded-lg font-semibold text-sm disabled:opacity-50"
                    style={bouton}
                  >
                    {envoyerMutation.isPending ? t('onboarding.welcome.verif.sending') : t('onboarding.welcome.verif.send')}
                  </button>
                ) : (
                  <>
                    {/* ph-mask : le libellé contient le cellulaire, masqué dans le replay. */}
                    <label className="ph-mask text-xs font-bold tracking-widest px-1" style={discret}>
                      {t('onboarding.welcome.verif.codeLabel', { phone: personalPhone })}
                    </label>
                    <div className="flex gap-2">
                      <input
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="123456"
                        className="flex-1 h-11 px-4 rounded-lg outline-none tracking-[0.3em] text-center text-lg"
                        style={champ}
                      />
                      <button
                        type="button"
                        onClick={() => verifierMutation.mutate()}
                        disabled={code.length !== 6 || verifierMutation.isPending}
                        className="h-11 px-4 rounded-lg font-semibold text-sm disabled:opacity-50"
                        style={bouton}
                      >
                        {verifierMutation.isPending ? '…' : t('onboarding.welcome.verif.check')}
                      </button>
                    </div>
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => envoyerMutation.mutate()}
                        disabled={attente > 0 || envoyerMutation.isPending}
                        className="text-xs text-left underline underline-offset-2 disabled:no-underline disabled:opacity-60"
                        style={discret}
                      >
                        {attente > 0 ? t('onboarding.welcome.verif.resendIn', { s: attente }) : t('onboarding.welcome.verif.resend')}
                      </button>
                      {/* Mauvaise saisie : on revient au champ, le code envoyé est oublié. */}
                      <button
                        type="button"
                        onClick={() => { setCode(''); setErreurCode(''); onEtape('idle') }}
                        className="text-xs text-left underline underline-offset-2"
                        style={discret}
                      >
                        {t('onboarding.welcome.verif.changeNumber', 'Changer de numéro')}
                      </button>
                    </div>
                  </>
                )}
                {erreurCode && <span className="text-xs text-red-400">{erreurCode}</span>}
              </div>
  )
}
